import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { getAllUsers, createUserWithRole, deleteUser } from "../services/userService";
import { getAllBooks, deleteBook, updateBook } from "../services/booksService";
import { generateBookPdf } from "../services/pdfService";
import PdfViewer from "../components/PdfViewer";

const AdminDashboard = () => {
    const navigate = useNavigate();
    
    // Core State
    const [activeTab, setActiveTab] = useState("overview"); // "overview" | "users" | "books"
    const [users, setUsers] = useState([]);
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Create User Form State
    const [newUserName, setNewUserName] = useState("");
    const [newUserEmail, setNewUserEmail] = useState("");
    const [newUserPassword, setNewUserPassword] = useState("");
    const [newUserRole, setNewUserRole] = useState("setter"); // "setter" | "proof reader" | "publisher"
    const [formSuccess, setFormSuccess] = useState("");
    const [formError, setFormError] = useState("");

    // Modal / Drawer States
    const [selectedUserBooks, setSelectedUserBooks] = useState(null); // User object for showing their books
    const [selectedPdfBook, setSelectedPdfBook] = useState(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [generatingPdf, setGeneratingPdf] = useState(false);

    // Search/Filters
    const [userSearch, setUserSearch] = useState("");
    const [bookSearch, setBookSearch] = useState("");
    const [bookFilterStatus, setBookFilterStatus] = useState("all");

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const fetchedUsers = await getAllUsers();
            const fetchedBooks = await getAllBooks();
            setUsers(fetchedUsers);
            setBooks(fetchedBooks);
        } catch (error) {
            console.error("Error loading admin dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/admin");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormError("");
        setFormSuccess("");
        setActionLoading(true);

        try {
            await createUserWithRole(
                newUserEmail,
                newUserPassword,
                newUserName,
                newUserRole
            );
            
            setFormSuccess(`User ${newUserName} created successfully as ${newUserRole}!`);
            
            // Clear inputs
            setNewUserName("");
            setNewUserEmail("");
            setNewUserPassword("");
            setNewUserRole("setter");

            // Reload data
            const updatedUsers = await getAllUsers();
            setUsers(updatedUsers);
        } catch (err) {
            console.error("Error creating user:", err);
            setFormError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, ""));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async (uid, name) => {
        if (!window.confirm(`Are you sure you want to delete user "${name}"? This only deletes their profile record in Firestore.`)) {
            return;
        }

        try {
            setActionLoading(true);
            await deleteUser(uid);
            setUsers(users.filter(u => u.uid !== uid));
        } catch (error) {
            console.error("Error deleting user:", error);
            alert("Failed to delete user profile.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteBook = async (bookId, title) => {
        if (!window.confirm(`Are you sure you want to delete the book "${title}"? This cannot be undone.`)) {
            return;
        }

        try {
            setActionLoading(true);
            await deleteBook(bookId);
            setBooks(books.filter(b => b.id !== bookId));
        } catch (error) {
            console.error("Error deleting book:", error);
            alert("Failed to delete book.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateBookStatus = async (bookId, newStatus) => {
        try {
            await updateBook(bookId, { status: newStatus });
            setBooks(books.map(b => b.id === bookId ? { ...b, status: newStatus } : b));
        } catch (error) {
            console.error("Error updating book status:", error);
            alert("Failed to update status.");
        }
    };

    const handlePreviewPdf = async (book) => {
        if (!book) return;
        try {
            setGeneratingPdf(true);
            setSelectedPdfBook(book);
            
            const pdfBytes = await generateBookPdf({
                title: book.title,
                author: book.author,
                content: book.manuscriptContent || "No manuscript content.",
                description: book.description,
                frontCover: book.frontCover,
                backCover: book.backCover,
                aboutAuthor: book.aboutAuthor,
                authorPhoto: book.authorPhoto,
                authorSignature: book.authorSignature,
                coverMode: book.coverMode,
                coverBgColor: book.coverBgColor,
                coverTextColor: book.coverTextColor,
                coverPosition: book.coverPosition,
                coverFont: book.coverFont,
                titleColor: book.titleColor,
                titleSize: book.titleSize,
                titleTop: book.titleTop,
                titleLeft: book.titleLeft,
                titleWidth: book.titleWidth,
                titleFontWeight: book.titleFontWeight,
                titleFontStyle: book.titleFontStyle,
                authorColor: book.authorColor,
                authorSize: book.authorSize,
                authorTop: book.authorTop,
                authorLeft: book.authorLeft,
                authorWidth: book.authorWidth,
                authorFontWeight: book.authorFontWeight,
                authorFontStyle: book.authorFontStyle,
                descColor: book.descColor,
                descSize: book.descSize,
                descTop: book.descTop,
                descLeft: book.descLeft,
                descWidth: book.descWidth,
                descFontWeight: book.descFontWeight,
                descFontStyle: book.descFontStyle,
                profileColor: book.profileColor,
                profileSize: book.profileSize,
                profileTop: book.profileTop,
                profileLeft: book.profileLeft,
                profileWidth: book.profileWidth,
                profileFontWeight: book.profileFontWeight,
                profileFontStyle: book.profileFontStyle,
                coverTitle: book.coverTitle,
                coverAuthor: book.coverAuthor,
                coverDescription: book.coverDescription,
                coverProfileText: book.coverProfileText,
                titleAlign: book.titleAlign,
                authorAlign: book.authorAlign,
                descAlign: book.descAlign,
                profileAlign: book.profileAlign,
                titleCase: book.titleCase,
                authorCase: book.authorCase,
                descCase: book.descCase,
                profileCase: book.profileCase,
                pageSize: book.pageSize,
                margins: book.margins,
                theme: book.theme,
                headerFooterEnabled: book.headerFooterEnabled,
            });

            const blob = new Blob([pdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (error) {
            console.error("PDF generation failed:", error);
            alert("Could not generate book PDF. Please ensure layout configuration is valid.");
            setSelectedPdfBook(null);
        } finally {
            setGeneratingPdf(false);
        }
    };

    const closePdfModal = () => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
        }
        setPdfUrl("");
        setSelectedPdfBook(null);
    };

    // Computations
    const roleStats = {
        admin: users.filter(u => u.role === "admin").length,
        setter: users.filter(u => u.role === "setter").length,
        proofreader: users.filter(u => u.role === "proof reader" || u.role === "proofreader").length,
        publisher: users.filter(u => u.role === "publisher").length,
        author: users.filter(u => u.role === "author" || !u.role).length,
    };

    const bookStats = {
        draft: books.filter(b => b.status === "draft").length,
        uploaded: books.filter(b => b.status === "manuscript-uploaded").length,
        published: books.filter(b => b.status === "published").length,
        total: books.length
    };

    const filteredUsers = users.filter(u => {
        const matchesSearch = 
            (u.name && u.name.toLowerCase().includes(userSearch.toLowerCase())) ||
            (u.email && u.email.toLowerCase().includes(userSearch.toLowerCase())) ||
            (u.role && u.role.toLowerCase().includes(userSearch.toLowerCase()));
        return matchesSearch;
    });

    const filteredBooks = books.filter(b => {
        const matchesSearch = 
            (b.title && b.title.toLowerCase().includes(bookSearch.toLowerCase())) ||
            (b.author && b.author.toLowerCase().includes(bookSearch.toLowerCase()));
        const matchesStatus = bookFilterStatus === "all" || b.status === bookFilterStatus;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-950 text-white font-sans">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-400 text-sm font-medium tracking-wide">Loading workspace data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex font-sans">
            
            {/* Sidebar Navigation */}
            <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col justify-between shrink-0">
                <div>
                    <div className="p-6 border-b border-slate-800 flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-tr from-rose-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md shadow-indigo-500/10">
                            <span className="text-xl">📚</span>
                        </div>
                        <div>
                            <h2 className="font-bold text-sm text-white tracking-wide">Admin Core</h2>
                            <p className="text-slate-500 text-xs">Book Platform v1.1</p>
                        </div>
                    </div>

                    <nav className="p-4 space-y-1">
                        <button
                            onClick={() => setActiveTab("overview")}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-150 ${
                                activeTab === "overview"
                                    ? "bg-gradient-to-r from-rose-500/10 to-indigo-500/10 border border-slate-700/50 text-rose-400"
                                    : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                            }`}
                        >
                            <span className="text-lg">📊</span>
                            Overview
                        </button>

                        <button
                            onClick={() => setActiveTab("users")}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-150 ${
                                activeTab === "users"
                                    ? "bg-gradient-to-r from-rose-500/10 to-indigo-500/10 border border-slate-700/50 text-rose-400"
                                    : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                            }`}
                        >
                            <span className="text-lg">👥</span>
                            User Management
                        </button>

                        <button
                            onClick={() => setActiveTab("books")}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-150 ${
                                activeTab === "books"
                                    ? "bg-gradient-to-r from-rose-500/10 to-indigo-500/10 border border-slate-700/50 text-rose-400"
                                    : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                            }`}
                        >
                            <span className="text-lg">📖</span>
                            Book Directory
                        </button>
                    </nav>
                </div>

                <div className="p-4 border-t border-slate-800">
                    <div className="bg-slate-950/60 rounded-xl p-3 border border-slate-800/60 mb-3">
                        <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Logged In As</p>
                        <p className="text-sm font-bold text-slate-200 truncate">{auth.currentUser?.email}</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 bg-slate-800/80 hover:bg-rose-550 hover:bg-rose-900/30 hover:text-rose-400 border border-slate-700/60 text-slate-300 font-semibold py-2.5 px-4 rounded-lg text-xs transition-colors duration-150"
                    >
                        🚪 Sign Out
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 overflow-y-auto p-8 relative">
                
                {/* Header */}
                <div className="flex justify-between items-center mb-8 border-b border-slate-900 pb-6">
                    <div>
                        <h1 className="text-3xl font-extrabold text-white capitalize tracking-tight">{activeTab} Panel</h1>
                        <p className="text-slate-400 text-sm mt-1">Monitor users, assign tasks, and audit publishing progress.</p>
                    </div>
                    <button 
                        onClick={loadData}
                        className="bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-200 font-semibold py-2 px-4 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
                    >
                        🔄 Refresh
                    </button>
                </div>

                {/* TAB 1: OVERVIEW */}
                {activeTab === "overview" && (
                    <div className="space-y-8">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-slate-900/60 border border-slate-850 p-6 rounded-2xl flex items-center justify-between shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500/5 rounded-full blur-xl"></div>
                                <div>
                                    <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Total Active Users</p>
                                    <h3 className="text-3xl font-bold text-white mt-2">{users.length}</h3>
                                </div>
                                <span className="text-3xl bg-blue-500/10 p-3 rounded-xl">👥</span>
                            </div>

                            <div className="bg-slate-900/60 border border-slate-850 p-6 rounded-2xl flex items-center justify-between shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-16 h-16 bg-rose-500/5 rounded-full blur-xl"></div>
                                <div>
                                    <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Total Manuscripts</p>
                                    <h3 className="text-3xl font-bold text-white mt-2">{books.length}</h3>
                                </div>
                                <span className="text-3xl bg-rose-500/10 p-3 rounded-xl">📖</span>
                            </div>

                            <div className="bg-slate-900/60 border border-slate-850 p-6 rounded-2xl flex items-center justify-between shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-500/5 rounded-full blur-xl"></div>
                                <div>
                                    <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Published Books</p>
                                    <h3 className="text-3xl font-bold text-white mt-2">{bookStats.published}</h3>
                                </div>
                                <span className="text-3xl bg-indigo-500/10 p-3 rounded-xl">🚀</span>
                            </div>

                            <div className="bg-slate-900/60 border border-slate-850 p-6 rounded-2xl flex items-center justify-between shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/5 rounded-full blur-xl"></div>
                                <div>
                                    <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Pending Work</p>
                                    <h3 className="text-3xl font-bold text-white mt-2">{bookStats.draft + bookStats.uploaded}</h3>
                                </div>
                                <span className="text-3xl bg-amber-500/10 p-3 rounded-xl">⏳</span>
                            </div>
                        </div>

                        {/* Distributions and Quick Lists */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            
                            {/* User Roles Breakdown */}
                            <div className="bg-slate-900/40 border border-slate-850 rounded-2xl p-6 lg:col-span-1">
                                <h3 className="text-lg font-bold text-white mb-6">Staff & Roles Distribution</h3>
                                <div className="space-y-5">
                                    {[
                                        { label: "Setters (Layout/Cover)", count: roleStats.setter, color: "bg-blue-500", raw: "setter" },
                                        { label: "Proof Readers", count: roleStats.proofreader, color: "bg-purple-500", raw: "proof reader" },
                                        { label: "Publishers (Final Print)", count: roleStats.publisher, color: "bg-emerald-500", raw: "publisher" },
                                        { label: "Authors (Self-register)", count: roleStats.author, color: "bg-slate-500", raw: "author" },
                                        { label: "Administrators", count: roleStats.admin, color: "bg-rose-500", raw: "admin" }
                                    ].map((role) => {
                                        const percentage = users.length > 0 ? (role.count / users.length) * 100 : 0;
                                        return (
                                            <div key={role.raw}>
                                                <div className="flex justify-between items-center text-xs font-medium text-slate-400 mb-1.5">
                                                    <span>{role.label}</span>
                                                    <span className="text-white font-semibold">{role.count} ({Math.round(percentage)}%)</span>
                                                </div>
                                                <div className="w-full bg-slate-800 rounded-full h-2">
                                                    <div 
                                                        className={`h-2 rounded-full ${role.color} transition-all duration-500`}
                                                        style={{ width: `${percentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Recent Books */}
                            <div className="bg-slate-900/40 border border-slate-850 rounded-2xl p-6 lg:col-span-2">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-bold text-white">Recent Manuscripts</h3>
                                    <button 
                                        onClick={() => setActiveTab("books")}
                                        className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold"
                                    >
                                        View all books →
                                    </button>
                                </div>
                                
                                {books.length === 0 ? (
                                    <div className="h-48 flex flex-col items-center justify-center text-slate-500 text-sm">
                                        <p>No books registered in the platform yet.</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-850">
                                        {books.slice(0, 5).map((book) => (
                                            <div key={book.id} className="py-3 flex justify-between items-center text-sm">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-12 bg-slate-800 border border-slate-700/60 rounded flex items-center justify-center text-[10px] overflow-hidden">
                                                        {book.frontCover ? (
                                                            <img src={book.frontCover} alt="Cover" className="w-full h-full object-cover" />
                                                        ) : (
                                                            "📘"
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-slate-200">{book.title || "Untitled Book"}</h4>
                                                        <p className="text-xs text-slate-500">By {book.author || "Unknown"}</p>
                                                    </div>
                                                </div>
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                                                    book.status === "published"
                                                        ? "bg-indigo-500/10 text-indigo-400 border border-indigo-500/20"
                                                        : book.status === "manuscript-uploaded"
                                                        ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                                        : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                                                }`}>
                                                    {book.status || "draft"}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 2: USER MANAGEMENT */}
                {activeTab === "users" && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                        
                        {/* Left column: Add User Form */}
                        <div className="bg-slate-900/60 border border-slate-850 p-6 rounded-2xl lg:col-span-1 space-y-6">
                            <div>
                                <h3 className="text-lg font-bold text-white">Create Platform User</h3>
                                <p className="text-xs text-slate-500 mt-1">Specify layout setters, proofreaders, or final publishers.</p>
                            </div>

                            {formSuccess && (
                                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs rounded-lg p-3">
                                    {formSuccess}
                                </div>
                            )}

                            {formError && (
                                <div className="bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs rounded-lg p-3">
                                    {formError}
                                </div>
                            )}

                            <form onSubmit={handleCreateUser} className="space-y-4">
                                <div>
                                    <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">Full Name</label>
                                    <input 
                                        type="text" 
                                        required
                                        placeholder="e.g. John Doe"
                                        value={newUserName}
                                        onChange={(e) => setNewUserName(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">Email Address</label>
                                    <input 
                                        type="email" 
                                        required
                                        placeholder="e.g. setter@bookplatform.com"
                                        value={newUserEmail}
                                        onChange={(e) => setNewUserEmail(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">Password</label>
                                    <input 
                                        type="password" 
                                        required
                                        placeholder="Min 6 characters"
                                        value={newUserPassword}
                                        onChange={(e) => setNewUserPassword(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">Platform Role</label>
                                    <select 
                                        value={newUserRole}
                                        onChange={(e) => setNewUserRole(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                    >
                                        <option value="setter">Setter (Cover & Layout)</option>
                                        <option value="proof reader">Proof Reader (Text & Edits)</option>
                                        <option value="publisher">Publisher (PDF compile & Publish)</option>
                                    </select>
                                </div>

                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="w-full bg-rose-650 hover:bg-rose-700 bg-gradient-to-r from-rose-500 to-indigo-650 text-white font-semibold py-2.5 px-4 rounded-lg text-xs transition-opacity duration-150 disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {actionLoading ? "Processing User..." : "Register New Account"}
                                </button>
                            </form>
                        </div>

                        {/* Right column: Users List Table */}
                        <div className="bg-slate-900/40 border border-slate-850 p-6 rounded-2xl lg:col-span-2 space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <h3 className="text-lg font-bold text-white">Registered Users Directory</h3>
                                <input
                                    type="text"
                                    placeholder="Search by name, email, role..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    className="bg-slate-950 border border-slate-800 rounded-lg py-1.5 px-3 text-xs placeholder-slate-500 text-white focus:outline-none focus:border-indigo-500"
                                />
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                                            <th className="pb-3">Name</th>
                                            <th className="pb-3">Email</th>
                                            <th className="pb-3">Role</th>
                                            <th className="pb-3">Joined Date</th>
                                            <th className="pb-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-850">
                                        {filteredUsers.map((user) => (
                                            <tr key={user.uid} className="hover:bg-slate-900/30 transition-colors">
                                                <td className="py-4 font-semibold text-slate-200">{user.name || "N/A"}</td>
                                                <td className="py-4 text-slate-400">{user.email}</td>
                                                <td className="py-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                                        user.role === "admin"
                                                            ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                                                            : user.role === "setter"
                                                            ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                                            : user.role === "proof reader" || user.role === "proofreader"
                                                            ? "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                                                            : user.role === "publisher"
                                                            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                                            : "bg-slate-500/10 text-slate-400 border border-slate-500/20"
                                                    }`}>
                                                        {user.role || "author"}
                                                    </span>
                                                </td>
                                                <td className="py-4 text-slate-500">
                                                    {user.createdAt?.seconds 
                                                        ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                                                        : "No record"}
                                                </td>
                                                <td className="py-4 text-right space-x-2">
                                                    <button
                                                        onClick={() => setSelectedUserBooks(user)}
                                                        className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold"
                                                    >
                                                        Books ({books.filter(b => b.userId === user.uid).length})
                                                    </button>
                                                    
                                                    {user.email !== "admin@bookplatform.com" && (
                                                        <button
                                                            onClick={() => handleDeleteUser(user.uid, user.name)}
                                                            className="text-xs text-rose-400 hover:text-rose-300 font-semibold"
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredUsers.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center py-6 text-slate-500">
                                                    No users found matching query.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 3: BOOK DIRECTORY */}
                {activeTab === "books" && (
                    <div className="bg-slate-900/40 border border-slate-850 p-6 rounded-2xl space-y-6">
                        
                        {/* Search and Filters */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <h3 className="text-lg font-bold text-white">All Platform Manuscripts</h3>
                                <div className="flex gap-2">
                                    {["all", "draft", "manuscript-uploaded", "published"].map(status => (
                                        <button
                                            key={status}
                                            onClick={() => setBookFilterStatus(status)}
                                            className={`px-3 py-1 rounded text-xs capitalize ${
                                                bookFilterStatus === status
                                                    ? "bg-indigo-600 text-white font-medium"
                                                    : "bg-slate-800 hover:bg-slate-700 text-slate-400"
                                            }`}
                                        >
                                            {status.replace("-", " ")}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <input
                                type="text"
                                placeholder="Search by title, author..."
                                value={bookSearch}
                                onChange={(e) => setBookSearch(e.target.value)}
                                className="bg-slate-950 border border-slate-800 rounded-lg py-1.5 px-3 text-xs placeholder-slate-500 text-white focus:outline-none focus:border-indigo-500 w-full sm:w-64"
                            />
                        </div>

                        {/* Books Grid */}
                        {filteredBooks.length === 0 ? (
                            <div className="h-64 flex flex-col items-center justify-center text-slate-500 text-sm">
                                <p>No manuscripts registered under this filter.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredBooks.map((book) => {
                                    const authorProfile = users.find(u => u.uid === book.userId);
                                    return (
                                        <div key={book.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex flex-col justify-between hover:border-slate-700 transition-all duration-200 shadow-md">
                                            <div className="space-y-4">
                                                {/* Cover & Title block */}
                                                <div className="flex gap-4">
                                                    <div className="w-16 h-24 bg-slate-950 border border-slate-800/80 rounded flex items-center justify-center text-xl overflow-hidden shrink-0">
                                                        {book.frontCover ? (
                                                            <img src={book.frontCover} alt="Cover Preview" className="w-full h-full object-cover" />
                                                        ) : (
                                                            "📘"
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-white text-sm line-clamp-2">{book.title || "Untitled Book"}</h4>
                                                        <p className="text-xs text-slate-400 mt-1">Author: {book.author || "N/A"}</p>
                                                        <p className="text-[10px] text-slate-500 truncate mt-0.5">{authorProfile?.email || "No Author Email"}</p>
                                                        
                                                        {/* Status selector */}
                                                        <div className="mt-2.5 flex items-center gap-1.5">
                                                            <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Status:</span>
                                                            <select
                                                                value={book.status || "draft"}
                                                                onChange={(e) => handleUpdateBookStatus(book.id, e.target.value)}
                                                                className="bg-slate-950 text-[10px] font-bold border border-slate-850 rounded px-1.5 py-0.5 text-rose-400 focus:outline-none uppercase tracking-wider"
                                                            >
                                                                <option value="draft">Draft</option>
                                                                <option value="manuscript-uploaded">Manuscript Uploaded</option>
                                                                <option value="published">Published</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-slate-850 pt-3">
                                                    <p className="text-xs text-slate-400 line-clamp-3 italic">
                                                        "{book.description || "No book synopsis configured."}"
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Action Bar */}
                                            <div className="flex gap-2 mt-5 pt-3 border-t border-slate-850">
                                                <button
                                                    onClick={() => handlePreviewPdf(book)}
                                                    className="flex-1 bg-indigo-600/10 border border-indigo-500/20 hover:bg-indigo-600/25 text-indigo-400 text-xs font-semibold py-1.5 px-3 rounded-lg transition-colors flex items-center justify-center gap-1"
                                                >
                                                    📄 PDF Print
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteBook(book.id, book.title)}
                                                    className="bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 text-rose-400 text-xs font-semibold py-1.5 px-3 rounded-lg transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* MODAL: VIEW USER'S BOOKS */}
                {selectedUserBooks && (
                    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-150">
                            <div className="p-6 border-b border-slate-850 flex justify-between items-center">
                                <div>
                                    <h3 className="text-lg font-bold text-white">Books by {selectedUserBooks.name || "User"}</h3>
                                    <p className="text-xs text-slate-400 mt-0.5">{selectedUserBooks.email}</p>
                                </div>
                                <button 
                                    onClick={() => setSelectedUserBooks(null)}
                                    className="w-8 w-8 bg-slate-800 hover:bg-slate-700 text-white rounded-full flex items-center justify-center text-sm"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-6 max-h-[500px] overflow-y-auto space-y-3">
                                {books.filter(b => b.userId === selectedUserBooks.uid).length === 0 ? (
                                    <p className="text-sm text-slate-500 text-center py-8">This user has not created any books yet.</p>
                                ) : (
                                    books.filter(b => b.userId === selectedUserBooks.uid).map((book) => (
                                        <div key={book.id} className="bg-slate-950 border border-slate-850 p-4 rounded-xl flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-14 bg-slate-800 border border-slate-700 rounded flex items-center justify-center text-xs overflow-hidden shrink-0">
                                                    {book.frontCover ? (
                                                        <img src={book.frontCover} alt="Cover" className="w-full h-full object-cover" />
                                                    ) : (
                                                        "📘"
                                                    )}
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-200 text-sm line-clamp-1">{book.title || "Untitled Book"}</h4>
                                                    <p className="text-xs text-slate-500 mt-0.5">Author: {book.author || "Unknown"}</p>
                                                    <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider font-semibold">Status: {book.status || "draft"}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedUserBooks(null);
                                                        handlePreviewPdf(book);
                                                    }}
                                                    className="bg-indigo-600/10 border border-indigo-500/20 hover:bg-indigo-600/25 text-indigo-400 text-xs font-semibold py-1.5 px-3 rounded-lg"
                                                >
                                                    PDF Preview
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteBook(book.id, book.title)}
                                                    className="bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 text-rose-400 text-xs font-semibold py-1.5 px-3 rounded-lg"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="p-4 bg-slate-950/40 border-t border-slate-850 text-right">
                                <button 
                                    onClick={() => setSelectedUserBooks(null)}
                                    className="bg-slate-800 hover:bg-slate-700 text-white font-semibold py-1.5 px-4 rounded-lg text-xs"
                                >
                                    Close Window
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL: PDF VIEWER OVERLAY */}
                {selectedPdfBook && (
                    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-150">
                            
                            <div className="p-5 border-b border-slate-850 flex justify-between items-center">
                                <div>
                                    <h3 className="text-lg font-bold text-white">Print-Ready PDF Compilation</h3>
                                    <p className="text-xs text-slate-400 mt-0.5">Title: {selectedPdfBook.title || "Untitled"}</p>
                                </div>
                                <button 
                                    onClick={closePdfModal}
                                    className="w-8 h-8 bg-slate-850 hover:bg-slate-700 text-white rounded-full flex items-center justify-center text-sm"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="bg-slate-950 flex items-center justify-center relative min-h-[500px]">
                                {generatingPdf ? (
                                    <div className="flex flex-col items-center gap-4 text-center">
                                        <div className="w-10 h-10 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-slate-400 text-xs font-semibold">Running layout calculations & compiling PDF-Lib layers...</p>
                                    </div>
                                ) : pdfUrl ? (
                                    <PdfViewer pdfUrl={pdfUrl} />
                                ) : (
                                    <div className="text-slate-500 text-xs">Failed to initialize PDF preview layers.</div>
                                )}
                            </div>

                            <div className="p-4 bg-slate-900 border-t border-slate-850 flex justify-between items-center">
                                <span className="text-[10px] text-slate-500 italic">Layout size: {selectedPdfBook.pageSize || "A5"} • Font: {selectedPdfBook.coverFont || "Times New Roman"}</span>
                                <div className="flex gap-2">
                                    {pdfUrl && (
                                        <a
                                            href={pdfUrl}
                                            download={`${selectedPdfBook.title?.replace(/\s+/g, "_") || "book"}_export.pdf`}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold py-1.5 px-4 rounded-lg flex items-center gap-1.5 transition-colors"
                                        >
                                            📥 Download Document
                                        </a>
                                    )}
                                    <button 
                                        onClick={closePdfModal}
                                        className="bg-slate-800 hover:bg-slate-750 text-white font-semibold py-1.5 px-4 rounded-lg text-xs"
                                    >
                                        Close Preview
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminDashboard;
